<template>
  <v-field :label variant="plain" dirty @click:clear="clear">
    <v-input
      :validation-value="date || ''"
      :hint="hint"
      :rules="[
        required,
        () => minDate ? minDateRule(minDate, model) : true,
        () => maxDate ? maxDateRule(maxDate, model) : true
      ]"
      density="compact"
      persistent-hint
    >
      <FieldTrain ltr>
        <AppSelect v-model="day" :items="DAYS" :placeholder="$tt('day')" hide-details>
          <template #item="{ item }">
            {{ formatDateNum(item.value) }}
          </template>
          <template #selection="{ item }">
            {{ formatDateNum(item.value) }}
          </template>
        </AppSelect>
        <AppSelect v-model="month" :items="MONTHS" :placeholder="$tt('month')" hide-details>
          <template #item="{ item }">
            {{ formatDateNum(item.value) }} - {{ formatMonth(item.value - 1) }}
          </template>
          <template #selection="{ item }">
            {{ formatDateNum(item.value) }}
          </template>
        </AppSelect>
        <AppSelect v-model="year" :items="years" :placeholder="$tt('year')" hide-details />
      </FieldTrain>
    </v-input>
  </v-field>
</template>

<script lang="ts">
const MIN_YEAR = 1920;
const MAX_YEAR = getCurrentYear() + 10;
const MONTHS = buildIncrementalArray(12, 1);
const DAYS = buildIncrementalArray(31, 1);
</script>

<script setup lang="ts">
import { dateToIso, getCurrentYear, isoToDate, isSameDay, nowDate } from '~/helpers/date';
import { formatDateNum } from '~/helpers/format';
import { buildIncrementalArray } from '~/helpers/general';
import type { DateISO } from '~/types';

const { minDateRule, maxDateRule, required } = useValidation();
const { formatMonth } = useFormat();

const model = defineModel<DateISO>({ required: true });
const props = defineProps<{
  label: string;
  hint?: string;
  past?: boolean;
  future?: boolean;
  max?: DateISO;
  min?: DateISO;
}>();

const date = computed(() => model.value ? isoToDate(model.value) : null);

const day = ref(date.value?.getDate());
const month = ref(date.value ? date.value.getMonth() + 1 : undefined);
const year = ref(date.value?.getFullYear());

const minDate = computed(() => props.min ? isoToDate(props.min) : props.future ? nowDate() : null);
const maxDate = computed(() => props.max ? isoToDate(props.max) : props.past ? nowDate() : null);
const years = computed(() => {
  const start = minDate.value?.getFullYear() || MIN_YEAR;
  const end = maxDate.value?.getFullYear() || MAX_YEAR;
  return buildIncrementalArray(end - start + 1, start);
});

watch([day, month, year], ([d, m, y]) => {
  if (!d || !m || !y) return;
  const date = new Date(y, m - 1, d);
  model.value = dateToIso(date);
});
watch(date, (date, prevDate) => {
  if (!date) return;
  if (prevDate && isSameDay(date, prevDate)) return;
  day.value = date.getDate();
  month.value = date.getMonth() + 1;
  year.value = date.getFullYear();
});

function clear() {
  model.value = '';
  day.value = month.value = year.value = undefined;
}
</script>

<style scoped>
:deep(.v-field-label--floating) {
  background-color: #fff;
  opacity: 1;
  color: gray;
  translate: 12px 4px;
  padding-inline: var(--space-1);
}
</style>